<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark>mdi-arrow-left</v-icon>
              </v-btn>
              <h3 class="font-weight-bold">
                {{
                  categoryInfo
                    ? `${categoryInfo.description} - Weekly Rewards`
                    : "Category Weekly Rewards"
                }}
              </h3>
              <v-spacer></v-spacer>
              <template v-if="isDeletePage() != true">
                <!-- Show Add button if no rewards exist -->
                <v-btn
                  v-if="weeklyRewards.length === 0"
                  class="mx-2 mb-2"
                  color="success"
                  small
                  :to="`/weekly-rewards/create?category=${$route.params.categoryId}`"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Rewards
                </v-btn>
                <!-- Show Edit button if rewards exist -->
                <v-btn
                  v-else
                  class="mx-2 mb-2"
                  color="primary"
                  small
                  :to="`/weekly-rewards/category/${$route.params.categoryId}/edit`"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  Edit Rewards
                </v-btn>
              </template>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-text-field
                    ref="expiry"
                    solo
                    rounded
                    v-model="filter.expiry_date"
                    type="date"
                    clearable
                    @click:clear="(filter.expiry_date = null), (page = 1)"
                    label="Expiry Date"
                    @input="getData"
                    prepend-inner-icon="mdi-calendar"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="285"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Name</th>
                      <th class="text-left" v-if="hasAnyCoinValues">Coin Value</th>
                      <th class="text-left" v-if="hasAnyStoreItems">Store Item</th>
                      <th class="text-left">Expiry Date</th>
                      <th class="text-left">Image</th>
                      <!-- <th class="text-right action_th">Action</th> -->
                    </tr>
                  </thead>
                  <tbody v-if="weeklyRewards.length != 0">
                    <tr
                      v-for="(reward, i) in weeklyRewards"
                      :key="`reward_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': weeklyRewards.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">
                        {{ i + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="reward.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Weekly Reward is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="reward.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Weekly Reward is Inactive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ reward.name || "No Name" }}
                      </td>
                      <td v-if="hasAnyCoinValues">
                        <v-chip
                          color="orange"
                          text-color="white"
                          small
                          v-if="reward.coin"
                        >
                          <v-icon left small>mdi-bitcoin</v-icon>
                          {{ reward.coin }}
                        </v-chip>
                        <span v-else>-</span>
                      </td>
                      <td v-if="hasAnyStoreItems">
                        {{ reward.storeItem ? reward.storeItem.name : "-" }}
                      </td>
                      <td>
                        {{ formatDate(reward.expiry_date) || "No Expiry" }}
                      </td>
                      <td>
                        <v-img
                          v-if="reward.image_path"
                          :src="`${mediaUrl}${reward.image_path}`"
                          height="40"
                          width="40"
                          contain
                        ></v-img>
                        <span v-else>-</span>
                      </td>
                      <!-- <td class="text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Delete_this(reward.id)"
                              >
                                <v-icon dark> mdi-delete </v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this Weekly Reward</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="ml-2"
                                fab
                                x-small
                                color="primary"
                                :to="`/weekly-rewards/${encryptIt(reward.id)}`"
                              >
                                <v-icon dark> mdi-pencil </v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this Weekly Reward</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Restore_this(reward.id)"
                              >
                                <v-icon dark> mdi-delete-restore </v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this Weekly Reward</span>
                          </v-tooltip>
                        </template>
                      </td> -->
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td :colspan="totalColumns" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Delete Success Dialog Box -->
      <v-dialog v-model="is_deleted_rewards" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Weekly Reward deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_rewards = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_rewards" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Weekly Reward restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_rewards = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "WeeklyRewardCategoryPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        expiry_date: "",
      },
      weeklyRewards: [],
      categoryInfo: null,
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      delete_rewards: false,
      restore_rewards: false,
      is_deleted_rewards: false,
      is_restored_rewards: false,
    };
  },
  computed: {
    hasAnyCoinValues() {
      return this.weeklyRewards.some(reward =>
        reward.coin !== null && reward.coin !== undefined && reward.coin !== '' && reward.coin > 0
      );
    },
    hasAnyStoreItems() {
      return this.weeklyRewards.some(reward =>
        reward.store_item_id !== null && reward.store_item_id !== undefined && reward.store_item_id !== '' && reward.store_item_id > 0
      );
    },
    totalColumns() {
      let count = 5; // Sr No, Status, Name, Expiry Date, Image
      if (this.hasAnyCoinValues) count++;
      if (this.hasAnyStoreItems) count++;
      return count;
    },
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.categoryId;

      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.expiry_date = this.filter.expiry_date
        ? this.filter.expiry_date
        : "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&expiry_date=" +
        this.filter.expiry_date +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const response = await axios.get(
          `/weekly_rewards/category/${encryptedId}?${query}`
        );
        this.weeklyRewards = response.data.data;
        this.categoryInfo = response.data.category;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast?.error("Failed to load weekly rewards data");
      }

      this.isLoading = false;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
      } catch (e) {
        return dateString;
      }
    },

    async Delete_this(id) {
      this.delete_rewards = true;
      try {
        await axios.post(`/weekly_rewards/delete/${this.encryptIt(id)}`);
        this.delete_rewards = false;
        this.is_deleted_rewards = true;
        this.getData();
      } catch (error) {
        console.error("Error deleting reward:", error);
        this.delete_rewards = false;
        this.$toast?.error("Failed to delete weekly reward");
      }
    },

    async Restore_this(id) {
      this.restore_rewards = true;
      try {
        await axios.post(`/weekly_rewards/restore/${this.encryptIt(id)}`);
        this.restore_rewards = false;
        this.is_restored_rewards = true;
        this.getData();
      } catch (error) {
        console.error("Error restoring reward:", error);
        this.restore_rewards = false;
        this.$toast?.error("Failed to restore weekly reward");
      }
    },
  },
};
</script>
